import { HttpContext, HttpInterceptorFn } from '@angular/common/http';

/**
 * consts
 */
import { 
  BYPASS_JWT_CONTEXT,
  BYPASS_APP_REQUEST_CONTEXT,
  BYPASS_SHOW_LOADING_CONTEXT,
  BYPASS_HIDE_LOADING_CONTEXT,
} from './consts';
import { BYPASS_SESSION_ABORT_CONTEXT } from '../../../services/session-orchestrator/consts/session-abort-context';

/**
 * utilities
 */
import { AppHttpUtils } from '../../../utilities';

/**
 * Bypass HTTP Interceptor
 * 
 * @function bypassInterceptor
 * @description Bypasses subsequent interceptors for specific requests.
 * Allows certain requests to skip JWT, language, and loading processing.
 * 
 * @param {HttpRequest<any>} req - The outgoing HTTP request
 * @param {HttpHandlerFn} next - The next interceptor in the chain
 * @returns {Observable<HttpEvent<any>>} The HTTP response with or without bypass
 * 
 * @example
 * ```typescript
 * // Bypass all interceptors for i18n requests
 * // GET ./i18n/en.json -> Bypasses JWT, Lang, Loading interceptors
 * 
 * // Bypass all interceptors for external URLs
 * this.http.get('https://api.external.com/data').subscribe();
 * 
 * // Bypass all interceptors using header
 * this.http.get('/api/special', {
 *   headers: { 'X-Bypass-Interceptors': 'true' }
 * }).subscribe();
 * ```
 * 
 * @since 1.0.0
 * @author SADIN Development Team
 */
export const bypassInterceptor: HttpInterceptorFn = (req, next) => {

  // Clone request
  let bypassReq = req.clone();
  
  // Bypass all interceptors for i18n translation files
  if (AppHttpUtils.isIn18nRequest(req.url)) {
    
    // Create context
    const context = new HttpContext();

    // Set bypass contexts
    context
      .set(BYPASS_JWT_CONTEXT, true)
      .set(BYPASS_APP_REQUEST_CONTEXT, true)
      .set(BYPASS_SHOW_LOADING_CONTEXT, true)
      .set(BYPASS_HIDE_LOADING_CONTEXT, true)
      .set(BYPASS_SESSION_ABORT_CONTEXT, true);

    bypassReq = bypassReq.clone({ context });

  }

  // bypass add host url
  if (AppHttpUtils.isHasHost(req.url)) {

    // Create context
    const context = new HttpContext();

    // Set bypass contexts
    context
      .set(BYPASS_JWT_CONTEXT, true)
      .set(BYPASS_APP_REQUEST_CONTEXT, true);

    // Clone request with context
    bypassReq = bypassReq.clone({ context });
  }
  
  // Return the request to the next interceptor
  return next(bypassReq);

};
